import { useCallback, useEffect, useMemo } from "react";

import CampaignIcon from "@mui/icons-material/Campaign";
import ChatIcon from "@mui/icons-material/Chat";
import FolderIcon from "@mui/icons-material/Folder";
import GridViewIcon from "@mui/icons-material/GridView";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import PersonIcon from "@mui/icons-material/Person";
import RequestQuoteIcon from "@mui/icons-material/RequestQuote";
import { Box, Grid, Skeleton, Typography } from "@mui/material";

import { FastActionsSlider } from "../components/FastActionsSlider";
import { RecentActivities } from "../components/RecentActivities";
import { SummaryCard } from "../components/SummaryCard";
import { useHome } from "./useHome";
import { supportNumber } from "../../utils/supportNumber";

export const Home = ({ setPage, splits, orders, setDeliverySelected }) => {
  const {
    banners,
    partnerName,
    handleGetSplits,
    splitsData,
    splitsLoading,
    handleGetOrders,
    ordersData,
    ordersLoading,
    anouncements,
    anouncementsLoading,
    bannersLoading,
    cons_boleto,
  } = useHome();

  const handleRedirectTo = useCallback(
    ({ path, external = false }) => {
      if (external) {
        window.open(path, "_blank");
        return;
      }

      setPage(path);
    },
    [setPage],
  );

  const summary = useMemo(() => {
    return [
      {
        title: "Boletos",
        value: splitsData?.length || 0,
        loading: splitsLoading,
        onClick: () => handleRedirectTo({ path: 1 }),
      },
      {
        title: "Entregas",
        value: ordersData?.length || 0,
        loading: ordersLoading,
        onClick: () => handleRedirectTo({ path: 2 }),
      },
      {
        title: "Comunicados",
        value: anouncements?.length || 0,
        loading: anouncementsLoading,
        onClick: () => handleRedirectTo({ path: 9 }),
      },
    ];
  }, [
    anouncements?.length,
    anouncementsLoading,
    ordersData?.length,
    ordersLoading,
    splitsData?.length,
    splitsLoading,
  ]);

  const fastActions = useMemo(() => {
    return [
      {
        title: "Suporte",
        icon: <ChatIcon sx={{ fontSize: 40, color: "grey.800" }} />,
        onClick: () =>
          handleRedirectTo({
            path: `https://api.whatsapp.com/send/?phone=${supportNumber.whatsApp}&text&type=phone_number&app_absent=0`,
            external: true,
          }),
      },
      {
        title: "Meus pedidos",
        icon: <LocalShippingIcon sx={{ fontSize: 40, color: "grey.800" }} />,
        onClick: () => handleRedirectTo({ path: 2 }),
      },
      {
        title: "Apoio Marketing",
        icon: <FolderIcon sx={{ fontSize: 40, color: "grey.800" }} />,
        onClick: () => handleRedirectTo({ path: 3 }),
      },
      {
        title: "Consumíveis",
        icon: <GridViewIcon sx={{ fontSize: 40, color: "grey.800" }} />,
        onClick: () => handleRedirectTo({ path: 7 }),
      },
      {
        title: "Ver boletos",
        icon: <RequestQuoteIcon sx={{ fontSize: 40, color: "grey.800" }} />,
        onClick: () => handleRedirectTo({ path: 1 }),
      },
      {
        title: "Conta e perfil",
        icon: <PersonIcon sx={{ fontSize: 40, color: "grey.800" }} />,
        onClick: () => handleRedirectTo({ path: 4 }),
      },
    ];
  }, [handleRedirectTo]);

  const activities = useMemo(() => {
    const sorted = (splitsData || [])
      .slice()
      .map((item) => ({
        ...item,
        parsedDate: (() => {
          const dateStr = item?.vencimento || item?.[2];
          const [day, month, year] = dateStr?.split("/") || [];
          return day && month && year
            ? new Date(`${year}-${month}-${day}`)
            : null;
        })(),
      }))
      .filter(
        (item) =>
          item.parsedDate instanceof Date && !Number.isNaN(item.parsedDate),
      )
      .sort((a, b) => a.parsedDate - b.parsedDate);

    const slipsActivities =
      sorted.slice(0, 3).map((slip) => ({
        title: `Boleto #${slip.nota}`,
        date: `Data de vencimento: ${slip.vencimento}`,
        icon: <RequestQuoteIcon sx={{ fontSize: 40, color: "grey.800" }} />,
        onClick: () =>
          cons_boleto(
            slip.nufin,
            slip.codbco,
            slip.status,
            slip.linha_digitavel,
            slip.nossonum,
          ),
      })) || [];

    const ordersActivities =
      ordersData?.slice?.(0, 3).map((order) => ({
        title: `Pedido #${order?.[1]}`,
        date: `Data de entrega: ${order?.[2]}`,
        icon: <LocalShippingIcon sx={{ fontSize: 40, color: "grey.800" }} />,
        onClick: () => {
          handleRedirectTo({ path: 2 });

          let productsArray = order[5].split(";");

          productsArray = productsArray.filter(
            (u, index) => productsArray.indexOf(u) === index,
          );

          setDeliverySelected({
            id: order[0],
            numnota: order[1],
            data: order[2],
            details: order[4],
            products:
              productsArray[0] +
              (productsArray.length > 1
                ? ` e mais ${productsArray.length - 1} produtos`
                : ""),
            productsArr: order[5],
            qtdsArr: order[6],
            seriesArr: order[7],
          });
        },
      })) || [];

    const announcementsActivities =
      anouncements?.slice?.(0, 3).map((announcement) => ({
        title: announcement.title,
        date: `Publicado em: ${announcement.publication}`,
        icon: <CampaignIcon sx={{ fontSize: 40, color: "grey.800" }} />,
        onClick: () => handleRedirectTo({ path: 9 }),
      })) || [];

    const combinedActivities = [
      ...slipsActivities,
      ...ordersActivities,
      ...announcementsActivities,
    ];

    return combinedActivities;
  }, [anouncements, cons_boleto, handleRedirectTo, ordersData, splitsData]);

  const handleRedirectBanner = useCallback(
    (link) => {
      if (!link) return;

      const isFullUrl = /^https?:\/\//.test(link);

      const internalPaths = {
        contaPerfil: 4,
      };

      const isInternalPath = Object?.keys?.(internalPaths)?.includes?.(link);

      if (!isFullUrl && !isInternalPath) return;

      let path = link;

      if (isInternalPath) {
        path = internalPaths[link];
      }

      handleRedirectTo({ path, external: isFullUrl });
    },
    [handleRedirectTo],
  );

  useEffect(() => {
    handleGetSplits({ splits });
  }, [handleGetSplits, splits]);

  useEffect(() => {
    handleGetOrders({ orders });
  }, [handleGetOrders, orders]);

  return (
    <Grid container>
      <Grid item xs={12} sm={12} md={10}>
        <Box
          height="100%"
          paddingInline={3}
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
        >
          <Box width="100%">
            <Typography component="h1" fontSize={20} fontWeight="bold">
              Olá, {partnerName || "Parceiro"}
            </Typography>

            <Box height="100%">
              <Box
                component="section"
                mb={{
                  xs: 5,
                  sm: 5,
                  md: 1,
                }}
              >
                <Box mt={3} mb={1}>
                  <Typography component="p" fontSize={14}>
                    RESUMO
                  </Typography>
                </Box>

                <Grid container spacing={2} sx={{ padding: "0 5px" }}>
                  {summary.map((item) => (
                    <Grid item xs={4}>
                      <Box
                        onClick={item.onClick}
                        sx={{ cursor: item.onClick ? "pointer" : "default" }}
                      >
                        <SummaryCard
                          title={item.title}
                          value={item.value}
                          loading={item.loading}
                        />
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Box>

              {activities?.length > 0 && (
                <Box
                  component="section"
                  mb={{
                    xs: 5,
                    sm: 5,
                    md: 1,
                  }}
                >
                  <Box mt={3} mb={1}>
                    <Typography component="p" fontSize={14}>
                      ATIVIDADES RECENTES
                    </Typography>
                  </Box>

                  <RecentActivities activities={activities} />
                </Box>
              )}

              <Box component="section" mt={activities?.length === 0 ? 3 : 0}>
                <Box mb={1}>
                  <Typography component="p" fontSize={14}>
                    AÇÕES RÁPIDAS
                  </Typography>
                </Box>

                <FastActionsSlider fastActions={fastActions} />
              </Box>
            </Box>
          </Box>
        </Box>
      </Grid>

      <Grid item xs={12} sm={12} md={2}>
        <Box
          height="100%"
          paddingLeft={{
            xs: 0,
            sm: 0,
            md: 2,
          }}
          mt={{
            xs: 4,
            sm: 4,
            md: 0,
          }}
          mb={{
            xs: 4,
            sm: 4,
            md: 0,
          }}
          sx={{
            paddingInline: {
              xs: 2,
              sm: 2,
              md: 0,
            },
          }}
        >
          <Box
            height="100%"
            display="flex"
            justifyContent="space-between"
            flexDirection="column"
            gap={2}
          >
            {!bannersLoading &&
              banners?.map((banner) => (
                <Box
                  key={banner.id}
                  onClick={() => handleRedirectBanner(banner.link)}
                  sx={{ cursor: banner.link ? "pointer" : "default" }}
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                >
                  <img
                    src={banner.image}
                    alt="Banner 1"
                    style={{
                      objectFit: "contain",
                      borderRadius: "10px",
                      height: "43vh",
                    }}
                  />
                </Box>
              ))}

            {bannersLoading && (
              <>
                <Skeleton variant="rounded" width="100%" height="43vh" />
                <Skeleton variant="rounded" width="100%" height="43vh" />
              </>
            )}
          </Box>
        </Box>
      </Grid>
    </Grid>
  );
};
